/* eslint-disable no-console -- need error log*/
import type {Dispatch, JSX, SetStateAction} from 'react'
import React, {useState, useEffect, useRef, useCallback} from 'react'
import {TextButton} from '@/products/flexible-web-toolkit/components/button/text-button'
import {ButtonIcons} from '@/components/button-icons/button-icon'
import {pushToDataLayer} from '@/utilities/analytics'
import {Alert} from '@/components/Alert'
import type {LocationProps, LocationState} from '@/utilities/types'
import {getCoords} from './location-functions'

interface SearchLocationInputProps {
	className?: string
	countries?: string[]
	setSearchLocation: Dispatch<SetStateAction<LocationProps>>
	label: string
	placeholderText: string
	currentLocationText: string
	isMapLoaded: boolean
	setShowLoadingOverlay?: Dispatch<SetStateAction<boolean>>
	setLocationState: Dispatch<SetStateAction<LocationState>>
	showLocationPrompt?: boolean
	setSearchLocationLatLong: Dispatch<SetStateAction<LocationProps>>
	searchLocationLatLong: LocationProps
	enableSearch: boolean
	permission: 'prompt' | 'denied' | 'granted'
}
export const SearchLocationInput = ({
	className,
	countries = [],
	setSearchLocation,
	label,
	placeholderText,
	currentLocationText,
	isMapLoaded,
	setShowLoadingOverlay,
	setLocationState,
	showLocationPrompt = false,
	setSearchLocationLatLong,
	searchLocationLatLong,
	enableSearch,
	permission,
}: SearchLocationInputProps): JSX.Element => {
	const [searchValue, setSearchValue] = useState(
		searchLocationLatLong.searchName || ''
	)
	const autoCompleteRef = useRef(null)
	const handleAutoComplete = (value: SetStateAction<string>): void => {
		const autoComplete = new window.google.maps.places.Autocomplete(
			autoCompleteRef.current as unknown as HTMLInputElement,
			{
				types: ['geocode'],
				componentRestrictions: {country: countries},
			}
		)
		setSearchValue(value)
		autoComplete.setFields(['formatted_address', 'geometry', 'name'])
		autoComplete.addListener('place_changed', () => {
			const addressObject = autoComplete.getPlace()
			if (!addressObject.geometry?.location) {
				console.error(
					`No details available for input: '${addressObject.name}'`
				)
				return
			}

			const searchedPlace = addressObject.formatted_address || ''
			setSearchLocation({
				lat: addressObject.geometry.location.lat(),
				lng: addressObject.geometry.location.lng(),
			})
			setSearchValue(searchedPlace || '')
			setSearchLocationLatLong({
				searchName: searchedPlace,
				lat: addressObject.geometry.location.lat(),
				lng: addressObject.geometry.location.lng(),
			})
			setLocationState({permission, enableSearch: true})
			pushToDataLayer({
				event: 'finder_usage',
				finder_interaction: 'Search',
				finder_url: window.location.href,
			})
		})
	}

	const setGeoLocation = useCallback(
		(coords: {latitude: number; longitude: number}): void => {
			const getAddressFromGeocode = (lat: number, lng: number): void => {
				if (isMapLoaded) {
					const geocoder = new window.google.maps.Geocoder()

					const latlng = {
						lat,
						lng,
					}
					geocoder
						.geocode({location: latlng})
						.then((response) => {
							if (response.results[0]) {
								setSearchValue(
									response.results[0].formatted_address
								)
							} else {
								// window.alert('No results found')
								Alert({
									message: 'No results found',
									type: 'error',
								})
							}
						})
						.catch((e: unknown) => {
							Alert({
								message: `Geocoder failed due to: ${
									(e as Error).message
								}`,
								type: 'error',
							})
						})
				}
			}

			setSearchLocation({lat: coords.latitude, lng: coords.longitude})
			setSearchLocationLatLong({
				searchName: '',
				lat: coords.latitude,
				lng: coords.longitude,
			})
			getAddressFromGeocode(coords.latitude, coords.longitude)
			setShowLoadingOverlay?.(false)
		},
		[
			setSearchLocation,
			setSearchLocationLatLong,
			isMapLoaded,
			setShowLoadingOverlay,
		]
	)
	const enableGeolocation = useCallback((): void => {
		setShowLoadingOverlay?.(true)
		navigator.geolocation.getCurrentPosition(
			({coords}) => {
				setGeoLocation(coords)
				setLocationState({
					permission: 'granted',
					enableSearch: true,
				})
			},
			(err) => {
				console.error(err)
				setShowLoadingOverlay?.(false)
				setLocationState({enableSearch, permission: 'denied'})
			}
		)

		pushToDataLayer({
			event: 'finder_usage',
			finder_interaction: 'Auto-search',
			finder_url: window.location.href,
		})
	}, [setGeoLocation, setLocationState, setShowLoadingOverlay, enableSearch])

	useEffect(() => {
		let permissionStatus: PermissionStatus
		navigator.permissions
			.query({name: 'geolocation'})
			.then((status) => {
				permissionStatus = status
				setLocationState({
					enableSearch,
					permission: status.state,
				})
				// ✅ Only trigger the browser popup if status is 'prompt'
				if (status.state === 'prompt') {
					navigator.geolocation.getCurrentPosition(
						(position) => {
							console.log('Geolocation success:', position)
							enableGeolocation()
						},
						(error) => {
							console.warn('Geolocation error:', error)
						}
					)
				}
				// Listen for changes
				permissionStatus.onchange = () => {
					setLocationState({
						enableSearch,
						permission: permissionStatus.state,
					})
					if (permissionStatus.state === 'granted') {
						enableGeolocation()
					}
				}
			})
			.catch((err) => {
				console.error('Error checking geolocation permission:', err)
			})
		return () => {
			permissionStatus.onchange = null
		}
	}, [
		enableGeolocation,
		setLocationState,
		showLocationPrompt,
		isMapLoaded,
		enableSearch,
	])

	useEffect(() => {
		if (!isMapLoaded) return
		void getCoords().then((coords) => {
			if (coords) {
				setShowLoadingOverlay?.(true)
				setGeoLocation(coords)
			}
		})
	}, [isMapLoaded, setGeoLocation, setShowLoadingOverlay])

	return (
		<div
			className={`flex items-center justify-center md:w-1/2 md:justify-start ${className}`}
		>
			<div className="flex w-full flex-col md:max-w-lg">
				<label
					className="mb-2 text-xs font-bold text-gray-500"
					htmlFor="search"
				>
					{label}
				</label>
				<div className="flex flex-col items-center space-x-2 md:mb-4 md:flex-row">
					<input
						className="mb-0 w-full border-elanco-blue p-5 text-sm placeholder-black md:w-3/5"
						data-clarity-mask="true"
						id="search"
						name="search"
						onChange={(e) => {
							handleAutoComplete(e.target.value)
						}}
						placeholder={placeholderText}
						ref={autoCompleteRef}
						value={searchValue}
					/>
					<TextButton
						as="a"
						className="mt-3 w-full not-italic md:mt-0 md:w-2/5"
						icon={ButtonIcons.Location}
						onClick={enableGeolocation}
					>
						<span>{currentLocationText}</span>
					</TextButton>
				</div>
			</div>
		</div>
	)
}
